// src/screens/NotificationsScreen.js
import React, { useEffect, useMemo, useState } from "react";
import Logo from "../assets/Logo.png";
import { 
  Bell, 
  Trash2, 
  Search as SearchIcon, 
  X, 
  Package, 
  ClipboardCheck, 
  Truck, 
  MessageSquare,
  AlertCircle,
  Calendar,
  User,
  Box,
  FileText,
  ShoppingBag,
  Filter,
  CheckCircle,
  XCircle,
  Eye,
  ChevronRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../components/notifications/NotificationsProvider";

// Helper function to safely convert to Date
const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value === "object") {
    if (value?.toDate) return value.toDate();
    if (value?.seconds) return new Date(value.seconds * 1000);
  }
  if (typeof value === "number") return new Date(value * 1000);
  if (typeof value === "string") return new Date(value);
  return null;
};

// Format time ago for display
const formatTimeAgo = (date) => {
  if (!date) return "";
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD}d ago`;
};

// Group notifications by time: Today, This Week, This Month, Earlier
const groupNotificationsByTime = (notifications) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisWeek = new Date(today);
  thisWeek.setDate(today.getDate() - 7);
  const thisMonth = new Date(today);
  thisMonth.setDate(today.getDate() - 30);

  const groups = {
    "Today": [],
    "This Week": [],
    "This Month": [],
    "Earlier": []
  };

  notifications.forEach(notification => {
    const createdAtDate = toDateSafe(notification.createdAt);
    if (!createdAtDate) {
      groups["Earlier"].push(notification);
      return;
    }

    if (createdAtDate >= today) {
      groups["Today"].push(notification);
    } else if (createdAtDate >= thisWeek) {
      groups["This Week"].push(notification);
    } else if (createdAtDate >= thisMonth) {
      groups["This Month"].push(notification);
    } else {
      groups["Earlier"].push(notification);
    }
  });

  return groups;
};

// Get appropriate icon for notification type
const getIconForType = (type) => {
  const iconMap = {
    // General notifications
    'general': Bell,
    'alert': AlertCircle,
    'message': MessageSquare,
    'system': Bell,
    
    // Sample orders / inventory
    'sample_order': Package,
    'sample_request': Box,
    'sample_shipped': Truck,
    'inventory': Package,
    
    // Tasks
    'task': ClipboardCheck,
    'todo': ClipboardCheck,
    'reminder': Calendar,
    
    // Orders
    'order': ShoppingBag,
    'order_shipped': Truck,
    'order_delivered': Truck,
    
    // Documents
    'document': FileText,
    'report': FileText,
    
    // User related
    'user': User,
    'team': User,
    
    // Default
    'default': Bell
  };

  return iconMap[type] || iconMap.default;
};

// Get color for notification type
const getColorForType = (type) => {
  const colorMap = {
    'general': 'bg-blue-100 text-blue-600',
    'alert': 'bg-red-100 text-red-600',
    'sample_order': 'bg-green-100 text-green-600',
    'task': 'bg-amber-100 text-amber-600',
    'order': 'bg-purple-100 text-purple-600',
    'user': 'bg-cyan-100 text-cyan-600',
    'default': 'bg-gray-100 text-gray-600'
  };

  return colorMap[type] || colorMap.default;
};

const getNotificationLink = (notification) => {
  if (notification?.linkPath) return notification.linkPath;

  const entityType = String(notification?.entityType || "").trim().toLowerCase();
  const entityId = String(notification?.entityId || "").trim();
  if (!entityId) return "";

  switch (entityType) {
    case "samplerequest":
    case "sample_request":
    case "sampleorder":
    case "sample_order":
      return `/sample-orders?requestId=${encodeURIComponent(entityId)}`;
    case "task":
      return `/tasks?taskId=${encodeURIComponent(entityId)}`;
    case "inventory":
    case "inventoryitem":
    case "inventory_item":
      return `/inventory?itemId=${encodeURIComponent(entityId)}`;
    case "account":
      return `/accounts?accountId=${encodeURIComponent(entityId)}`;
    case "order":
      return `/orders?orderId=${encodeURIComponent(entityId)}`;
    default:
      return "";
  }
};

const getNotificationAvatar = (notification) => {
  const imageUrl =
    notification?.createdByPhotoURL ||
    notification?.userPhotoURL ||
    notification?.photoURL ||
    "";

  const nameCandidate =
    notification?.createdByName ||
    notification?.userName ||
    notification?.recipientName ||
    "";

  const normalizedName = String(nameCandidate || "").trim();
  const isSystem =
    !imageUrl &&
    (
      !notification?.createdByUid ||
      /supplyhawk|system/i.test(normalizedName) ||
      ["system", "general", "alert"].includes(String(notification?.type || "").toLowerCase())
    );

  return {
    imageUrl: imageUrl || (isSystem ? Logo : ""),
    name: normalizedName || (isSystem ? "SupplyHawk" : ""),
    isSystem,
  };
};

const formatTypeLabel = (type) => {
  if (!type) return "General";
  return type
    .toString()
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const PAGE_SIZE = 20;

const NotificationsScreen = ({ companyId: companyIdProp }) => {
  const navigate = useNavigate();

  const {
    currentUserId,
    companyId: companyIdCtx,
    requireNotificationSeen,
    notifications,
    loading,
    markAsRead,
    markAllAsRead,
    deleteOne,
    clearAll,
    retentionDays,
  } = useNotifications();

  const companyId = companyIdProp || companyIdCtx || null;

  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [markingAll, setMarkingAll] = useState(false);

  useEffect(() => setPage(0), [searchTerm, typeFilter, statusFilter, companyId]);

  const visibleNotifications = useMemo(() => {
    if (!companyId) return notifications;
    return notifications.filter((n) => !n.companyId || n.companyId === companyId);
  }, [notifications, companyId]);

  const visibleUnreadCount = useMemo(() => {
    return visibleNotifications.filter((n) => !n.isRead).length;
  }, [visibleNotifications]);

  const typeOptions = useMemo(() => {
    const set = new Set(['all']);
    visibleNotifications.forEach((n) => n.type && set.add(n.type));
    return Array.from(set);
  }, [visibleNotifications]);

  const filteredNotifications = useMemo(() => {
    let items = visibleNotifications;

    if (typeFilter !== "all") {
      items = items.filter((n) => (n.type || "").toLowerCase() === typeFilter.toLowerCase());
    }

    if (statusFilter !== "all") {
      items = items.filter((n) => 
        statusFilter === "unread" ? !n.isRead : n.isRead
      );
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      items = items.filter((n) => {
        const title = (n.title || "").toLowerCase();
        const body = (n.body || "").toLowerCase();
        const userName = (n.userName || "").toLowerCase();
        const type = (n.type || "").toLowerCase();
        return title.includes(term) || body.includes(term) || userName.includes(term) || type.includes(term);
      });
    }

    return items;
  }, [visibleNotifications, typeFilter, statusFilter, searchTerm]);

  const groupedNotifications = useMemo(() => {
    return groupNotificationsByTime(filteredNotifications);
  }, [filteredNotifications]);

  const total = filteredNotifications.length;
  const totalPages = total === 0 ? 1 : Math.ceil(total / PAGE_SIZE);
  const startIndex = page * PAGE_SIZE;
  const handleClearAll = async () => {
    if (!currentUserId || visibleNotifications.length === 0) return;

    const confirmed = window.confirm(
      "Clear all notifications? This will permanently delete all your notifications."
    );
    if (!confirmed) return;

    await clearAll();
  };

  const handleMarkAllAsSeen = async () => {
    if (!currentUserId || visibleUnreadCount === 0) return;

    try {
      setMarkingAll(true);
      await markAllAsRead();
    } finally {
      setMarkingAll(false);
    }
  };

  const handleOpenRelated = async (notification) => {
    if (!currentUserId || !notification?.id) return;

    try {
      if (!requireNotificationSeen && !notification.isRead) {
        await markAsRead(notification.id);
      }

      const nextLink = getNotificationLink(notification);
      if (nextLink) {
        navigate(nextLink);
      }
    } catch (err) {
      console.error("[NotificationsScreen] Error handling click:", err);
    }
  };

  const renderNotificationItem = (notification) => {
    const createdAtDate = toDateSafe(notification.createdAt);
    const timeAgo = formatTimeAgo(createdAtDate);
    const IconComponent = getIconForType(notification.type);
    const colorClass = getColorForType(notification.type);
    const { imageUrl, name, isSystem } = getNotificationAvatar(notification);
    const linkPath = getNotificationLink(notification);

    return (
      <div
        key={notification.id}
        className={`px-3 py-2.5 flex gap-3 transition-colors ${
          !notification.isRead ? 'bg-blue-50 border-l-2 border-blue-500' : ''
        }`}
      >
        <button
          type="button"
          onClick={() => handleOpenRelated(notification)}
          className={`flex min-w-0 flex-1 items-start gap-3 rounded-lg px-1 py-1 text-left transition-colors ${linkPath ? "hover:bg-gray-50" : ""}`}
        >
          <div className="flex-shrink-0">
            <div className="relative">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={name || (isSystem ? "SupplyHawk" : "User")}
                  className={`h-9 w-9 rounded-full border border-white object-cover shadow-sm ${isSystem ? "bg-white p-1" : ""}`}
                />
              ) : (
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-100 to-slate-200 flex items-center justify-center border border-white">
                  <User className="h-4 w-4 text-gray-500" />
                </div>
              )}

              <div className={`absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white flex items-center justify-center ${colorClass}`}>
                <IconComponent className="h-2.5 w-2.5" />
              </div>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className={`truncate text-sm font-medium ${!notification.isRead ? 'text-gray-900' : 'text-gray-700'}`}>
                    {notification.title || "Notification"}
                  </p>
                  {!notification.isRead && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-blue-600" />}
                </div>
                {notification.body ? (
                  <p className="mt-0.5 line-clamp-2 text-xs text-gray-600">{notification.body}</p>
                ) : null}
                <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-500">
                  {name ? <span className="truncate">{name}</span> : null}
                  {timeAgo ? <span>{timeAgo}</span> : null}
                </div>
              </div>
              {linkPath ? <ChevronRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-300" /> : null}
            </div>
          </div>
        </button>

        <div className="flex items-center gap-1">
          {requireNotificationSeen && !notification.isRead && (
            <button
              type="button"
              onClick={() => markAsRead(notification.id)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-green-200 text-green-600 hover:bg-green-50 transition-colors"
              title="Mark as seen"
            >
              <Eye className="h-4 w-4" />
            </button>
          )}

          <button
            type="button"
            onClick={() => deleteOne(notification.id)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors"
            title="Delete notification"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  if (!currentUserId) {
    return (
      <div className="max-w-6xl mx-auto px-3 py-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Bell className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Please log in</h2>
          <p className="text-sm text-gray-600 mb-4">You need to be logged in to view your notifications.</p>
          <button
            onClick={() => navigate("/login")}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-3 md:px-4 py-3 md:py-4 space-y-4">
      {/* Header Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shadow-md">
                <Bell className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Notifications</h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm text-gray-600">
                    {visibleUnreadCount > 0 
                      ? `${visibleUnreadCount} unread notification${visibleUnreadCount !== 1 ? 's' : ''}`
                      : "All caught up!"
                    }
                  </span>
                  <span className="text-xs text-gray-400">•</span>
                  <span className="text-xs text-gray-500">
                    Auto-delete after {retentionDays || 30} days
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {visibleUnreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllAsSeen}
                  disabled={markingAll}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    markingAll
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  {markingAll ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Marking...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Mark All As Seen
                    </>
                  )}
                </button>
              )}

              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                Back to Dashboard
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon className="w-5 h-5 text-gray-400" />
                </span>
                <input
                  type="text"
                  placeholder="Search notifications by title, message, or user..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-200 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <X className="w-5 h-5 text-gray-300 hover:text-gray-500" />
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Filter className="w-4 h-4 text-gray-400" />
                </span>
                <select
                  className="w-full pl-9 pr-4 py-3 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="all">All types</option>
                  {typeOptions.filter(t => t !== 'all').map((t) => (
                    <option key={t} value={t}>
                      {formatTypeLabel(t)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <select
                  className="w-full pl-3 pr-4 py-3 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All status</option>
                  <option value="unread">Unread only</option>
                  <option value="read">Read only</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4 animate-pulse">
              <Bell className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">Loading your notifications...</p>
          </div>
        ) : total === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No notifications yet</h3>
            <p className="text-sm text-gray-600 mb-4">
              {searchTerm || typeFilter !== "all" || statusFilter !== "all"
                ? "No notifications match your current filters."
                : "You're all caught up! New notifications will appear here."}
            </p>
            {(searchTerm || typeFilter !== "all" || statusFilter !== "all") && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setTypeFilter("all");
                  setStatusFilter("all");
                }}
                className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Grouped Notifications */}
            <div className="divide-y divide-gray-100">
              {Object.entries(groupedNotifications).map(([groupName, groupNotifications]) => 
                groupNotifications.length > 0 && (
                  <div key={groupName} className="bg-gray-50">
                    <div className="sticky top-0 z-10 bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-700">
                          {groupName} • {groupNotifications.length} notification{groupNotifications.length !== 1 ? 's' : ''}
                        </h3>
                        <span className="text-xs text-gray-500">
                          {groupNotifications.filter(n => !n.isRead).length} unread
                        </span>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {groupNotifications.map((notification) => renderNotificationItem(notification))}
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Pagination */}
            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-gray-600">
                    Showing <span className="font-semibold">{startIndex + 1}-{Math.min(startIndex + PAGE_SIZE, total)}</span> of <span className="font-semibold">{total}</span> notifications
                  </p>
                  {visibleUnreadCount > 0 && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      {visibleUnreadCount} unread
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      page === 0
                        ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                        : "text-gray-700 bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    Previous
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i;
                      } else if (page < 3) {
                        pageNum = i;
                      } else if (page > totalPages - 4) {
                        pageNum = totalPages - 5 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          type="button"
                          onClick={() => setPage(pageNum)}
                          className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                            page === pageNum
                              ? "bg-blue-600 text-white"
                              : "text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {pageNum + 1}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={() => setPage((p) => (p + 1 >= totalPages ? p : p + 1))}
                    disabled={page + 1 >= totalPages}
                    className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      page + 1 >= totalPages
                        ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                        : "text-gray-700 bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Clear All Button */}
            {visibleNotifications.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Total: <span className="font-semibold">{visibleNotifications.length}</span> notification{visibleNotifications.length !== 1 ? 's' : ''}
                    {visibleUnreadCount > 0 && (
                      <span className="ml-2 text-red-600">
                        ({visibleUnreadCount} unread)
                      </span>
                    )}
                  </p>
                  
                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 hover:text-red-800 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Clear All Notifications
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsScreen;
